import { useMemo } from 'react'
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/utils/cn'
import { dayKey } from './dealsCalendarUtils'

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']

type DealsCalendarGridProps = {
  viewMonth: Date
  onViewMonthChange: (month: Date) => void
  selectedDay: Date | null
  onSelectDay: (day: Date) => void
  dealCountByDay: Map<string, number>
}

export function DealsCalendarGrid({
  viewMonth,
  onViewMonthChange,
  selectedDay,
  onSelectDay,
  dealCountByDay,
}: DealsCalendarGridProps) {
  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(viewMonth), { weekStartsOn: 0 })
    const end = endOfWeek(endOfMonth(viewMonth), { weekStartsOn: 0 })
    return eachDayOfInterval({ start, end })
  }, [viewMonth])

  const monthDealTotal = useMemo(() => {
    let n = 0
    for (const [key, count] of dealCountByDay) {
      const d = new Date(`${key}T12:00:00`)
      if (isSameMonth(d, viewMonth)) n += count
    }
    return n
  }, [dealCountByDay, viewMonth])

  return (
    <div className='glass-card overflow-hidden p-4 md:p-6'>
      <div className='mb-4 flex flex-wrap items-center justify-between gap-3'>
        <div>
          <p className='text-xs font-semibold uppercase tracking-wider text-text-muted'>
            Calendário de deals
          </p>
          <h2 className='text-lg font-bold capitalize text-text-primary md:text-xl'>
            {format(viewMonth, 'MMMM yyyy', { locale: ptBR })}
          </h2>
        </div>
        <div className='flex items-center gap-2'>
          <span className='rounded-full bg-white/[0.06] px-3 py-1 text-xs text-text-muted'>
            {monthDealTotal} {monthDealTotal === 1 ? 'deal no mês' : 'deals no mês'}
          </span>
          <button
            type='button'
            aria-label='Mês anterior'
            onClick={() => onViewMonthChange(subMonths(viewMonth, 1))}
            className='rounded-xl border border-[var(--border)] p-2 text-text-muted transition-colors hover:bg-white/[0.06] hover:text-text-primary'
          >
            <ChevronLeft size={20} />
          </button>
          <button
            type='button'
            aria-label='Próximo mês'
            onClick={() => onViewMonthChange(addMonths(viewMonth, 1))}
            className='rounded-xl border border-[var(--border)] p-2 text-text-muted transition-colors hover:bg-white/[0.06] hover:text-text-primary'
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>

      <div className='mb-2 grid grid-cols-7 gap-1 md:gap-2'>
        {WEEKDAYS.map((label) => (
          <span
            key={label}
            className='py-2 text-center text-[11px] font-bold uppercase tracking-wide text-text-muted md:text-xs'
          >
            {label}
          </span>
        ))}
      </div>

      <div className='grid grid-cols-7 gap-1 md:gap-2'>
        {days.map((day) => {
          const key = dayKey(day)
          const count = dealCountByDay.get(key) ?? 0
          const outside = !isSameMonth(day, viewMonth)
          const selected = selectedDay ? isSameDay(day, selectedDay) : false
          const today = isSameDay(day, new Date())

          return (
            <button
              key={key}
              type='button'
              onClick={() => onSelectDay(day)}
              className={cn(
                'relative flex min-h-[52px] flex-col items-center justify-center rounded-xl border transition-all md:min-h-[72px] lg:min-h-[88px]',
                outside && 'border-transparent bg-transparent opacity-40',
                !outside &&
                  !selected &&
                  'border-[var(--border)] bg-white/[0.02] hover:border-accent/30 hover:bg-accent/[0.06]',
                selected &&
                  'border-accent bg-accent/15 shadow-[0_0_0_1px_rgba(var(--accent-rgb,99,102,241),0.25)]',
                today && !selected && 'ring-1 ring-accent/40',
              )}
            >
              <span
                className={cn(
                  'text-sm font-semibold md:text-base lg:text-lg',
                  selected ? 'text-accent' : 'text-text-primary',
                  outside && 'text-text-muted',
                )}
              >
                {format(day, 'd')}
              </span>
              {count > 0 && (
                <span
                  className={cn(
                    'mt-0.5 rounded-full px-1.5 py-0.5 text-[9px] font-bold md:text-[10px]',
                    selected
                      ? 'bg-accent text-white'
                      : 'bg-accent/20 text-accent',
                  )}
                >
                  {count}
                </span>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
